import React, { useState, useEffect } from "react";
import { Form, Col, Container, Button, Row } from "react-bootstrap";
import Select from "react-select";
import DataTable from "react-data-table-component";
import { REPORTS } from "../../../configurations/Urls";
import {
  ELECTRIC_PROVIDERS,
  EMPTY,
  INFO,
  STAFF_SESSION_EXPIRED,
} from "../../../configurations/Constants";
import WellReportService from "../../../api-service/WellReportService";
import _ from "lodash";
import SessionHelper from "../../../helpers/SessionHelper";
import { debounce } from "lodash";
import WellConnectionUpdate from "./WellConnectionUpdate";
import StorageHelper from "../../../helpers/StorageHelper";
import LoadingOverlay from "../../common/LoadingOverlay";
import MessageBanner from "../../common/MessageBanner";
import {
  ERROR_DOWNLOADING_WELL_REPORT,
  ERROR_LOADING_WELL_IRRIG_UNIT_LIST,
  INCOMING_TOKEN_HAS_EXPIRED,
  STAFF_AUTHORIZED,
  WELL_DETAILS_UPDATED_SUCCESSFULLY,
} from "../../../configurations/MessageConstants";
import Alert from "../../common/Alert";
import { StatusCodes } from "../../../configurations/status-codes";

const SearchBox = ({ onSearch, value }) => (
  <Form.Control
    type="text"
    defaultValue={value}
    name="searchValue"
    onChange={onSearch}
    placeholder="Search"
    className="form-control form-control-lg me-2 w-50"
  />
);

const Export = ({ onExport }) => (
  <Button
    onClick={(e) => onExport(e.target.value)}
    className="d-flex align-items-center"
  >
    Export <i className="fa fa-download ms-2" aria-hidden="true"></i>
  </Button>
);

function WellConnectionReport(props) {
  const [irrigUnitId, setIrrigUnitId] = useState(null);
  const [wellId, setWellId] = useState(null);
  const [wellActive, setWellActive] = useState(null);
  const [wellConnected, setWellConnected] = useState(null);
  const [wellElectric, setWellElectric] = useState(null);
  const [powerCompany, setPowerCompany] = useState(null);
  const [searchValue, setSearchValue] = useState(null);
  const [column, setColumn] = useState(null);
  const [columnOrder, setColumnOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [exportURL, setExportURL] = useState(EMPTY);
  const [overlayLoading, setOverlayLoading] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [selectedWell, setSelectedWell] = useState(null);
  const [data, setData] = useState([]);
  const [allWellOptions, setAllWellOptions] = useState([]);
  const [allIrrigUnitOptions, setAllIrrigUnitOptions] = useState([]);
  const [wellConnectionDetailsUpdated, setWellConnectionDetailsUpdated] =
    useState(false);
  const columns = [
    {
      id: 1,
      cell: (row) => (
        <Button
          onClick={() => updateWellConnectionDetails(row)}
          id={row.well_id}
          className="well-connection-update-button"
        >
          Update
        </Button>
      ),
      width: "6rem",
    },
    {
      id: 2,
      name: "Irrig Unit ID",
      selector: (row) => row.irrig_unit_id,
      sortable: true,
      reorder: true,
      width: "8rem",
      value: "irrig_unit_id",
    },
    {
      id: 3,
      name: "Well ID",
      selector: (row) => row.well_id,
      sortable: true,
      reorder: true,
      width: "8rem",
      value: "well_id",
    },
    {
      id: 4,
      name: "Well Active",
      selector: (row) => row.is_active_icon,
      sortable: true,
      reorder: true,
      width: "8rem",
      value: "is_active",
    },
    {
      id: 5,
      name: "Connected",
      selector: (row) => row.is_connected_icon,
      sortable: true,
      reorder: true,
      width: "8rem",
      value: "is_connected",
    },
    {
      id: 6,
      name: "Electric",
      selector: (row) => row.is_electric_icon,
      sortable: true,
      reorder: true,
      width: "8rem",
      value: "is_electric",
    },

    {
      id: 7,
      name: "Power Co",
      selector: (row) => row.power_co,
      sortable: true,
      reorder: true,
      width: "10rem",
      value: "power_co",
    },

    {
      id: 8,
      name: "Meter ID",
      selector: (row) => row.meter_id,
      sortable: true,
      reorder: true,
      width: "8rem",
      value: "meter_id",
    },
    {
      id: 9,
      name: "Account ID",
      selector: (row) => row.account_id,
      sortable: true,
      reorder: true,
      width: "8rem",
      value: "account_id",
    },
    {
      id: 10,
      name: "Field Name",
      selector: (row) => row.field_name,
      sortable: true,
      reorder: true,
      width: "10rem",
      value: "field_name",
    },
    {
      id: 11,
      name: "Name",
      selector: (row) => row.user_name,
      sortable: true,
      reorder: true,
      width: "10rem",
      value: "user_name",
    },
    {
      id: 12,
      name: "Email",
      selector: (row) => row.email,
      sortable: true,
      reorder: true,
      width: "17rem",
      value: "email",
    },
  ];

  useEffect(() => {
    (async function () {
      try {
        setLoading(true);
        await Promise.all([
          getWellAndIrrigsData(),
          fetchWellConnectionReport(
            currentPage,
            perPage,
            irrigUnitId,
            wellId,
            wellActive,
            wellConnected,
            wellElectric,
            powerCompany,
            searchValue,
            column,
            columnOrder
          ),
        ]);
      } catch (error) {
        console.error(ERROR_LOADING_WELL_IRRIG_UNIT_LIST, error);
        return false;
      }
    })();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      await fetchWellConnectionReport(
        currentPage,
        perPage,
        irrigUnitId,
        wellId,
        wellActive,
        wellConnected,
        wellElectric,
        powerCompany,
        searchValue,
        column,
        columnOrder
      );
    };

    fetchData();
  }, [
    currentPage,
    perPage,
    irrigUnitId,
    wellId,
    wellActive,
    wellConnected,
    wellElectric,
    powerCompany,
    searchValue,
    column,
    columnOrder,
  ]);

  useEffect(() => {
    if (wellConnectionDetailsUpdated) {
      const fetchData = async () => {
        await fetchWellConnectionReport(
          currentPage,
          perPage,
          irrigUnitId,
          wellId,
          wellActive,
          wellConnected,
          wellElectric,
          powerCompany,
          searchValue,
          column,
          columnOrder
        );
      };
      fetchData();
    }
    setWellConnectionDetailsUpdated(false);
  }, [wellConnectionDetailsUpdated]);

  const getWellAndIrrigsData = async () => {
    let allWellsAndIrrigsData = {};
    try {
      let response = await WellReportService.getWellAndIrrigs();
      allWellsAndIrrigsData = response.data.data;
      generateWellAndIrrigsArray(allWellsAndIrrigsData);
    } catch (error) {
      console.error(ERROR_LOADING_WELL_IRRIG_UNIT_LIST, error);
      return false;
    }
  };

  const generateWellAndIrrigsArray = async (wellsAndIrrigs) => {
    let WellsArray = [];
    let IrrigUnitsArray = [];
    _.forEach(wellsAndIrrigs.irrig_wells, (value) => {
      let wellsObj = {};
      wellsObj.value = value;
      wellsObj.label = value;
      WellsArray.push(wellsObj);
    });
    _.forEach(wellsAndIrrigs.irrig_units, (value) => {
      let IrrigsObj = {};
      IrrigsObj.value = value;
      IrrigsObj.label = value;
      IrrigUnitsArray.push(IrrigsObj);
    });
    setAllWellOptions(WellsArray);
    setAllIrrigUnitOptions(IrrigUnitsArray);
  };

  const fetchWellConnectionReport = async (
    page,
    size,
    irrigUnitId,
    wellId,
    wellActive,
    wellConnected,
    wellElectric,
    powerCompany,
    searchValue,
    column,
    columnOrder
  ) => {
    setLoading(true);
    let params = `?current_pg=${page}`;
    if (size) {
      params = `${params}&rows_per_pg=${size}`;
    }
    if (irrigUnitId !== null) {
      params = `${params}&irrig_unit_id=${irrigUnitId.value}`;
    }
    if (wellId !== null) {
      params = `${params}&well_id=${wellId.value}`;
    }
    if (wellActive !== null) {
      params = `${params}&is_active=${wellActive.value}`;
    }
    if (wellConnected !== null) {
      params = `${params}&is_connected=${wellConnected.value}`;
    }
    if (wellElectric !== null) {
      params = `${params}&is_electric=${wellElectric.value}`;
    }
    if (powerCompany !== null) {
      params = `${params}&power_co=${powerCompany.value}`;
    }
    if (searchValue) {
      params = `${params}&search_text=${searchValue}`;
    }
    if (column !== null) {
      params = `${params}&column=${column}`;
    }
    if (columnOrder !== null) {
      params = `${params}&column_order=${columnOrder}`;
    }
    let url = `${REPORTS}/connections${params}`;
    let exporturl = `${REPORTS}/connections/export${params}`;
    try {
      const response = await WellReportService.getWellReports(url);
      let reportData = response.data.data;
      if (reportData) {
        _.forEach(reportData.data, (value) => {
          value.is_active_icon = handleTableIcons(value.is_active);
          value.is_connected_icon = handleTableIcons(value.is_connected);
          value.is_electric_icon = handleTableIcons(value.is_electric);
        });
        setExportURL(exporturl);
        setData(reportData.data);
        setTotalRows(reportData.totalRows);
        setLoading(false);
      }
    } catch (error) {
      let responseData = error.response.data;
      if (
        error.response.status === StatusCodes.UNAUTHORIZED &&
        responseData.message.includes(INCOMING_TOKEN_HAS_EXPIRED)
      ) {
        StorageHelper.setItem(STAFF_SESSION_EXPIRED, true);
        SessionHelper.cleanUpStorageKeys();
        window.location.reload();
      }
    }
  };

  const handleTableIcons = (value) => {
    if (!value) {
      return (
        <i
          className="fa-solid fa-xmark staff-report-cross"
          aria-hidden="true"
        ></i>
      );
    }
    return (
      <i className="fa-solid fa-check staff-report-tick" aria-hidden="true"></i>
    );
  };

  const handleSearchValue = (e) => {
    debouncedSearch(e.target.value);
  };

  const debouncedSearch = debounce((value) => {
    setSearchValue(value);
  }, 400);

  const handleSort = (column, sortDirection) => {
    setColumn(column.value);
    setColumnOrder(sortDirection);
  };

  const downloadCSV = async (url) => {
    setOverlayLoading(true);
    try {
      const response = await WellReportService.exportReports(url);
      const filenameRegex = /filename="(.*)"/;
      const matches = filenameRegex.exec(
        response.headers["content-disposition"]
      );
      const fileName = matches && matches[1] ? matches[1] : "well_report.xlsx";
      let blob = await response.data;
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = fileName;
      link.click();
      setOverlayLoading(false);
    } catch (error) {
      setOverlayLoading(false);

      let responseData = error.response.data;
      if (
        error.response.status === StatusCodes.UNAUTHORIZED &&
        responseData.message.includes(INCOMING_TOKEN_HAS_EXPIRED)
      ) {
        StorageHelper.setItem(STAFF_SESSION_EXPIRED, true);
        SessionHelper.cleanUpStorageKeys();
        window.location.reload();
      }
      console.error(ERROR_DOWNLOADING_WELL_REPORT, error);
    }
  };

  const closeUpdateModal = () => {
    setShowUpdateModal(false);
  };
  const updateWellConnectionDetails = (value) => {
    setShowUpdateModal(true);
    setSelectedWell(value);
  };
  const actions = (
    <div className="d-flex justify-content-end flex-grow-1 gap-2 well-connection-report-actions">
      <SearchBox onSearch={(e) => handleSearchValue(e, searchValue)} />
      <Export onExport={() => downloadCSV(exportURL)} />
    </div>
  );

  return (
    <div className="well-connection-report">
      <LoadingOverlay
        showSpinner={true}
        active={overlayLoading}
        text="Exporting Data..."
      />
      <MessageBanner
        title={"Irrigation Well Connection Report"}
        description={STAFF_AUTHORIZED}
      />
      <Container className="p-3 staff-report-body">
        <Row>
          <Form.Group as={Col} controlId="IrrigUnitId" md={2}>
            <Select
              isClearable={true}
              name="irrigUnitId"
              options={allIrrigUnitOptions}
              className="basic-multi-select well-connection-report-filters"
              onChange={setIrrigUnitId}
              value={irrigUnitId}
              placeholder={
                <div>
                  <span className="staff-report-filter-placeholder-large">
                    Irrig Unit ID
                  </span>
                  <span className="staff-report-filter-placeholder-small">
                    Irrig ID
                  </span>
                </div>
              }
            />
          </Form.Group>
          <Form.Group as={Col} controlId="WellRegistration" md={2}>
            <Select
              isClearable={true}
              name="wellId"
              options={allWellOptions}
              className="basic-multi-select well-connection-report-filters"
              onChange={setWellId}
              value={wellId}
              placeholder="Well ID"
            />
          </Form.Group>
          <Form.Group as={Col} controlId="WellIsActive" md={2}>
            <Select
              isClearable={true}
              name="wellActive"
              options={[
                { value: true, label: "Yes" },
                { value: false, label: "No" },
              ]}
              className="basic-multi-select well-connection-report-filters"
              onChange={setWellActive}
              value={wellActive}
              isSearchable={false}
              placeholder={
                <div>
                  <span className="staff-report-filter-placeholder-large">
                    Well Active
                  </span>
                  <span className="staff-report-filter-placeholder-small">
                    Active
                  </span>
                </div>
              }
            />
          </Form.Group>

          <Form.Group as={Col} controlId="WellIsConnected" md={2}>
            <Select
              isClearable={true}
              name="WellConnected"
              options={[
                { value: true, label: "Yes" },
                { value: false, label: "No" },
              ]}
              className="basic-multi-select well-connection-report-filters"
              onChange={setWellConnected}
              value={wellConnected}
              isSearchable={false}
              placeholder={
                <div>
                  <span className="staff-report-filter-placeholder-larger">
                    Well Connected
                  </span>
                  <span className="staff-report-filter-placeholder-smaller">
                    Connect
                  </span>
                </div>
              }
            />
          </Form.Group>
          <Form.Group as={Col} controlId="WellIsElectric" md={2}>
            <Select
              isClearable={true}
              name="WellElectric"
              options={[
                { value: true, label: "Yes" },
                { value: false, label: "No" },
              ]}
              className="basic-multi-select well-connection-report-filters"
              onChange={setWellElectric}
              value={wellElectric}
              isSearchable={false}
              placeholder={
                <div>
                  <span className="staff-report-filter-placeholder-large">
                    Well Electric
                  </span>
                  <span className="staff-report-filter-placeholder-small">
                    Electric
                  </span>
                </div>
              }
            />
          </Form.Group>
          <Form.Group as={Col} controlId="PowerCompany" md={2}>
            <Select
              isClearable={true}
              name="PowerCompany"
              options={ELECTRIC_PROVIDERS}
              className="basic-multi-select well-connection-report-filters"
              onChange={setPowerCompany}
              value={powerCompany}
              isSearchable={false}
              placeholder={
                <div>
                  <span className="staff-report-filter-placeholder-larger">
                    Power Company
                  </span>
                  <span className="staff-report-filter-placeholder-smaller">
                    Company
                  </span>
                </div>
              }
            />
          </Form.Group>
        </Row>
        <Row>
          <DataTable
            title="Well Connection Report"
            columns={columns}
            data={data}
            actions={actions}
            progressPending={loading}
            pagination
            paginationServer
            sortServer
            onSort={handleSort}
            paginationTotalRows={totalRows}
            paginationDefaultPage={currentPage}
            onChangeRowsPerPage={setPerPage}
            onChangePage={setCurrentPage}
            paginationRowsPerPageOptions={[10, 25, 50, 100]}
          />
        </Row>
      </Container>
      {showUpdateModal && (
        <WellConnectionUpdate
          showUpdateModal={showUpdateModal}
          closeUpdateModal={closeUpdateModal}
          selectedWell={selectedWell}
          setWellConnectionDetailsUpdated={setWellConnectionDetailsUpdated}
        />
      )}
    </div>
  );
}

export default WellConnectionReport;
